import { useCallback, useMemo, useRef, useState } from 'react';

import { formatOverlappingActivities } from 'common/OverlappingActivitiesBanner/helpers/overlappingActivities';
import { OVERLAPPING_ACTION_OPTIONS } from 'common/OverlappingActivitiesBanner/text';

const DELAY_ERROR_TOO_SMALL = 'The entered delay is too small.';
const DELAY_ERROR_TOO_LARGE = 'The entered delay is too large.';

const useOverlappingBanner = (baseTimezone) => {
	const [overlappingActivities, setActivities] = useState([]);
	const [hasOverlappingActivities, setHasOverlappingActivities] = useState(false);
	const [cannotCancelOverlaps, setCannotCancelOverlaps] = useState(false);
	const [overlappingAction, setAction] = useState('');
	const [delayMinutes, setDelayMinutes] = useState(null);
	const [minDelayMinutes, setMinDelayMinutes] = useState(null);
	const [maxDelayMinutes, setMaxDelayMinutes] = useState(null);
	const minDelayRef = useRef(null);

	const setOverlappingAction = useCallback((action) => {
		setAction(action);
		if (action === OVERLAPPING_ACTION_OPTIONS.DELAY) {
			setDelayMinutes(minDelayRef.current);
		}
	}, []);

	const setOverlappingActivities = useCallback(
		(activities, { minDelay = null, maxDelay = null } = {}) => {
			const hasActivities = activities.length > 0;
			const cannotCancel = hasActivities && activities.some((a) => !a.isCancellable);
			setHasOverlappingActivities(hasActivities);
			setActivities(formatOverlappingActivities(activities, baseTimezone) || []);
			setCannotCancelOverlaps(cannotCancel);
			setMinDelayMinutes(minDelay);
			setMaxDelayMinutes(maxDelay);
			minDelayRef.current = minDelay;
			if (minDelay != null) {
				setDelayMinutes(minDelay);
			}
		},
		[baseTimezone]
	);

	const resetOverlappingActivities = useCallback(() => {
		setHasOverlappingActivities(false);
		setActivities([]);
		setCannotCancelOverlaps(false);
		setAction('');
		setDelayMinutes(null);
		setMinDelayMinutes(null);
		setMaxDelayMinutes(null);
		minDelayRef.current = null;
	}, []);

	const isDelayOptionAvailable = minDelayMinutes != null;
	const allowToDeleteOverlaps = overlappingAction === OVERLAPPING_ACTION_OPTIONS.DELETE;
	const allowToDelayOverlaps = overlappingAction === OVERLAPPING_ACTION_OPTIONS.DELAY;

	const delayError = useMemo(() => {
		if (!allowToDelayOverlaps || delayMinutes == null) return null;
		if (minDelayMinutes != null && delayMinutes < minDelayMinutes) return DELAY_ERROR_TOO_SMALL;
		if (maxDelayMinutes != null && delayMinutes > maxDelayMinutes) return DELAY_ERROR_TOO_LARGE;
		return null;
	}, [allowToDelayOverlaps, delayMinutes, minDelayMinutes, maxDelayMinutes]);

	return {
		overlappingActivities,
		hasOverlappingActivities,
		cannotCancelOverlaps,
		overlappingAction,
		isDelayOptionAvailable,
		allowToDeleteOverlaps,
		allowToDelayOverlaps,
		delayMinutes,
		delayError,
		minDelayMinutes,
		maxDelayMinutes,
		setDelayMinutes,
		setOverlappingAction,
		setOverlappingActivities,
		resetOverlappingActivities,
	};
};

export default useOverlappingBanner;
